import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import { InvitationsInvitationFragment } from "@/generated/graphql";

/**
 * Props for the ReceivedInvitationItemView component.
 */
interface Props {
  senderNameDisplay: string;
  item: InvitationsInvitationFragment;
  loading: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * View component for displaying a received invitation with accept and decline action buttons.
 * @param root0 - The component props
 * @param root0.senderNameDisplay - The formatted display name of the user who sent the invitation
 * @param root0.item - The invitation data fragment containing organization name, role, and status
 * @param root0.loading - Boolean indicating if accept or decline operation is in progress
 * @param root0.onAccept - Callback function to handle accepting the invitation
 * @param root0.onDecline - Callback function to handle declining the invitation
 */
const ReceivedInvitationItemView = ({
  senderNameDisplay,
  item,
  loading,
  onAccept,
  onDecline,
}: Props) => (
  <Stack direction="horizontal" className="items-center gap-4 py-4">
    <Box className="flex-1 gap-2">
      <Text variant="display-sm">{item.user?.organization?.name}</Text>
      <Text variant="unstyled" className="font-body text-title">
        invited by <Text variant="title">{senderNameDisplay}</Text>
      </Text>
    </Box>
    {item.status === "PENDING" && (
      <Stack className="gap-4">
        <Button
          variant="outline"
          onPress={onAccept}
          isDisabled={loading}
          label="Accept"
        />
        <Button
          variant="outline"
          onPress={onDecline}
          isDisabled={loading}
          label="Decline"
        />
      </Stack>
    )}
  </Stack>
);

ReceivedInvitationItemView.displayName = "ReceivedInvitationItemView";

export default memo(ReceivedInvitationItemView);
